import { CloseRounded } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  Divider,
  IconButton,
  TextField as MuiTextField,
  Typography,
  styled,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { getDistrictMasterData, getStateMasterData } from "../../../../services/master/master_service";
import { HospitalType, MasterOption } from "../../model/hospital";
import { black, greyEnabled, purple, purpleLight } from "../../../../theme/colors";

export interface HospitalsFilterValues {
  hospitalType: HospitalType | null;
  stateId: string | null;
  districtId: string | null;
  fromDate: string;
  toDate: string;
}

interface FilterHospitalsProps extends HospitalsFilterValues {
  open: boolean;
  onClose: () => void;
  onApply: (filters: HospitalsFilterValues) => void;
}

const HOSPITAL_TYPE_OPTIONS: { value: HospitalType; label: string }[] = [
  { value: "government", label: "Government" },
  { value: "private", label: "Private" },
  { value: "trust", label: "Trust" },
];

const popoverPaperSx = {
  elevation: 0,
  sx: {
    mt: 0.5,
    borderRadius: 2,
    border: "1px solid #ECECEC",
    boxShadow: "0px 12px 28px -8px rgba(27, 0, 27, 0.18)",
  },
} as const;

const TextField = styled(MuiTextField)(() => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    "& fieldset": { borderColor: "#ABA0AB" },
    "&:hover fieldset": { borderColor: purple },
    "&.Mui-focused fieldset": { borderColor: purple, borderWidth: 2 },
  },
}));

export default function FilterHospitals({
  open,
  onClose,
  hospitalType,
  stateId,
  districtId,
  fromDate,
  toDate,
  onApply,
}: FilterHospitalsProps) {
  const [_hospitalType, _setHospitalType] = useState<HospitalType | null>(hospitalType);
  const [_state, _setState] = useState<MasterOption | null>(null);
  const [_district, _setDistrict] = useState<MasterOption | null>(null);
  const [_fromDate, _setFromDate] = useState<string>(fromDate);
  const [_toDate, _setToDate] = useState<string>(toDate);

  const { data: stateList } = useQuery(["state-master-list"], async () => {
    let response = await getStateMasterData();
    return (response?.data?.data?.list || []) as MasterOption[];
  });

  const { data: districtList } = useQuery(
    ["district-master-list", _state?.id],
    async () => {
      let response = await getDistrictMasterData(_state!.id);
      return (response?.data?.data?.list || []) as MasterOption[];
    },
    { enabled: !!_state }
  );

  // Resolve the currently-applied stateId/districtId (raw string ids) against
  // the master lists once they've loaded, so re-opening the dialog shows the
  // previously selected filters instead of a blank Autocomplete.
  useEffect(() => {
    if (stateList && stateId && !_state) {
      const match = stateList.find((item) => String(item.id) === stateId);
      if (match) _setState(match);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateList]);

  useEffect(() => {
    if (districtList && districtId && !_district) {
      const match = districtList.find((item) => String(item.id) === districtId);
      if (match) _setDistrict(match);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [districtList]);

  useEffect(() => {
    // Ensure end date is not before start date
    if (_toDate && _fromDate && dayjs(_fromDate, "YYYY-MM-DD").isAfter(dayjs(_toDate, "YYYY-MM-DD"))) {
      _setToDate("");
    }
  }, [_fromDate, _toDate]);

  const handleClose = () => onClose();

  const handleReset = () => {
    onApply({
      hospitalType: null,
      stateId: null,
      districtId: null,
      fromDate: "",
      toDate: "",
    });
    onClose();
  };

  const handleApply = () => {
    onApply({
      hospitalType: _hospitalType,
      stateId: _state ? String(_state.id) : null,
      districtId: _district ? String(_district.id) : null,
      fromDate: _fromDate,
      toDate: _toDate,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} PaperProps={{ sx: { borderRadius: 4 } }}>
      <Box sx={{ width: 440, maxWidth: "90vw" }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: "1px solid #ECECEC" }}
        >
          <Typography variant="h6" fontWeight={700} color={black}>
            Filter Hospitals
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseRounded sx={{ color: greyEnabled }} />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, pt: 2, pb: 3, maxHeight: "70vh", overflow: "auto" }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" fontWeight={600} color={black}>
              Filter By
            </Typography>
            <Button
              variant="text"
              sx={{ p: 0, minWidth: "auto", color: purple, textTransform: "none", fontWeight: 600 }}
              onClick={handleReset}
            >
              Reset
            </Button>
          </Box>
          <Divider sx={{ mt: 1.5, mb: 2 }} />

          <Typography variant="body2" fontWeight={600} color={black} mb={0.5}>
            Hospital Type
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
            {HOSPITAL_TYPE_OPTIONS.map((option) => (
              <Chip
                key={option.value}
                label={option.label}
                clickable
                variant={_hospitalType === option.value ? "filled" : "outlined"}
                color="primary"
                onClick={() =>
                  _setHospitalType((prev) => (prev === option.value ? null : option.value))
                }
              />
            ))}
          </Box>

          <Typography variant="body2" fontWeight={600} color={black} mb={0.5}>
            State
          </Typography>
          <Autocomplete
            fullWidth
            size="small"
            value={_state}
            options={stateList || []}
            getOptionLabel={(option) => option.value}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            componentsProps={{ paper: popoverPaperSx }}
            onChange={(_, value) => {
              _setState(value);
              _setDistrict(null);
            }}
            renderInput={(params) => <TextField {...params} placeholder="Select state" />}
          />

          <Typography variant="body2" fontWeight={600} color={black} mt={2} mb={0.5}>
            District
          </Typography>
          <Autocomplete
            fullWidth
            size="small"
            disabled={!_state}
            value={_district}
            options={districtList || []}
            getOptionLabel={(option) => option.value}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            componentsProps={{ paper: popoverPaperSx }}
            onChange={(_, value) => _setDistrict(value)}
            renderInput={(params) => (
              <TextField {...params} placeholder={_state ? "Select district" : "Select a state first"} />
            )}
          />

          <Typography variant="body2" fontWeight={600} color={black} mt={2} mb={1}>
            Uploaded Date
          </Typography>
          <Box display="flex" gap={1.5}>
            <DatePicker
              label="From Date"
              format="DD-MM-YYYY"
              disableFuture
              value={_fromDate ? dayjs(_fromDate, "YYYY-MM-DD") : null}
              onChange={(value: Dayjs | null) => {
                _setFromDate(value?.format("YYYY-MM-DD") || "");
              }}
              slotProps={{ desktopPaper: popoverPaperSx, textField: { size: "small" } }}
              sx={{ "& .MuiSvgIcon-root": { fill: purple } }}
            />
            <DatePicker
              label="To Date"
              format="DD-MM-YYYY"
              disableFuture
              minDate={_fromDate ? dayjs(_fromDate, "YYYY-MM-DD") : undefined}
              value={_toDate ? dayjs(_toDate, "YYYY-MM-DD") : null}
              onChange={(value: Dayjs | null) => {
                _setToDate(value?.format("YYYY-MM-DD") || "");
              }}
              slotProps={{ desktopPaper: popoverPaperSx, textField: { size: "small" } }}
              sx={{ "& .MuiSvgIcon-root": { fill: purple } }}
            />
          </Box>

          <Box display="flex" gap={1.5} mt={4}>
            <Button
              fullWidth
              onClick={handleClose}
              variant="outlined"
              sx={{
                borderColor: purple,
                color: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { borderColor: purple, bgcolor: purpleLight },
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              onClick={handleApply}
              sx={{
                bgcolor: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "#7A2074" },
              }}
            >
              Apply Filters
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
